import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import groupService from "../../services/UserGroupsService";

interface Group {
  id: number;
  title: string;
}

interface GroupState {
  groups: Group[];
  error?: string | null;
  loading: boolean;
}

const initialState: GroupState = {
  groups: [],
  error: null,
  loading: false,
};

export const fetchGroups = createAsyncThunk(
  "groups/fetchGroups",
  async (_, { rejectWithValue }) => {
    try {
      const response = await groupService.getAll();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Could not load user groups"
      );
    }
  }
);

export const addGroup = createAsyncThunk(
  "groups/addGroup",
  async (data: any) => {
    try {
      const response = await groupService.post(data);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

export const updateGroup = createAsyncThunk(
  "groups/updateGroup",
  async (data: any) => {
    try {
      const response = await groupService.put(data.id, data);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

export const removeGroup = createAsyncThunk(
  "groups/removeGroup",
  async (id: number) => {
    try {
      const response = await groupService.remove(id);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

const groupSlice = createSlice({
  name: "groups",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchGroups.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchGroups.fulfilled, (state, action) => {
      state.groups = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchGroups.rejected, (state, action) => {
      state.error = action.error.message;
      state.loading = false;
    });

    builder.addCase(addGroup.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addGroup.fulfilled, (state, action) => {
      state.groups.push(action.payload);
      state.loading = false;
    });
    builder.addCase(addGroup.rejected, (state, action) => {
      state.error = action.error.message;
      state.loading = false;
    });

    builder.addCase(updateGroup.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateGroup.fulfilled, (state, action) => {
      state.groups = state.groups.map((group) =>
        group.id === action.payload.id ? action.payload : group
      );
      state.loading = false;
    });
    builder.addCase(updateGroup.rejected, (state, action) => {
      state.error = action.error.message;
      state.loading = false;
    });

    builder.addCase(removeGroup.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(removeGroup.fulfilled, (state, action) => {
      state.groups = state.groups.filter(
        (group) => group.id !== action.payload.id
      );
      state.loading = false;
    });
    builder.addCase(removeGroup.rejected, (state, action) => {
      state.error = action.error.message;
      state.loading = false;
    });
  },
});

const { reducer } = groupSlice;
export default reducer;
